/**
 * SessionReviewPage — page post-session dédiée à la notation.
 *
 * Route : /session/:booking_id/review
 * Accessible uniquement si :
 *   - l'utilisateur est le client du booking (ownership check backend)
 *   - la séance est en status `completed`
 *
 * Design premium centré : carte 420 max-width, avatar coach, 5 étoiles XXL
 * interactives avec hover preview, commentaire optionnel, feedback visuel
 * sur l'envoi. Pre-sélection via querystring ?rating=N (venant de l'email).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ArrowLeft, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import { formatDateLongFR } from '../utils/formatFR';

const API = getApiUrl();

export default function SessionReviewPage() {
  const { booking_id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(Number(params.get('rating')) || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/marketplace/bookings/${booking_id}/review-context`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Accès refusé');
        }
        setContext(await res.json());
      } catch (err) {
        toast.error(err.message);
        navigate('/my-bookings');
      } finally {
        setLoading(false);
      }
    })();
  }, [booking_id, token, navigate]);

  const submit = async () => {
    if (rating < 1) { toast.error('Sélectionnez une note'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/marketplace/bookings/${booking_id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating, comment: comment.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setSuccess(true);
      setTimeout(() => navigate('/my-bookings'), 2500);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1C1147] via-[#2D1B69] to-[#1C1147]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!context) return null;

  if (context.already_rated || success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-[#1C1147] via-[#2D1B69] to-[#1C1147]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#1C1147] mb-2">Merci pour votre retour !</h1>
          <p className="text-sm text-[#4B3869] mb-6">
            Votre avis contribue à maintenir un haut niveau de qualité sur Coach DIA.
          </p>
          <button
            onClick={() => navigate('/my-bookings')}
            data-testid="review-done-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-[#1A0F3A] font-semibold hover:bg-[#F3D060] active:scale-95 transition-all"
          >
            Retour à mes séances
          </button>
        </motion.div>
      </div>
    );
  }

  const coachInitials = (context.coach_name || 'C')
    .split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');

  const ratingMeta = [
    { stars: 1, label: 'Décevant', color: 'text-red-400' },
    { stars: 2, label: 'Passable', color: 'text-amber-400' },
    { stars: 3, label: 'Correct', color: 'text-[#D4AF37]' },
    { stars: 4, label: 'Très bon', color: 'text-emerald-400' },
    { stars: 5, label: 'Excellent', color: 'text-[#D4AF37]' },
  ];
  const activeMeta = ratingMeta[(hover || rating) - 1];

  return (
    <div className="min-h-screen px-4 py-8 bg-gradient-to-br from-[#1C1147] via-[#2D1B69] to-[#1C1147]">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-6 transition-colors"
          data-testid="review-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Plus tard
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-3xl p-8 shadow-2xl"
          data-testid="review-card"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="uppercase tracking-[0.2em] text-[10px] text-[#D4AF37] font-bold mb-3">
              Avis post-session
            </div>
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#6D28D9] to-[#1C1147] flex items-center justify-center text-white font-bold text-lg shadow-lg">
              {coachInitials}
            </div>
            <h1 className="text-xl font-bold text-[#1C1147] leading-tight">
              Comment s'est passée votre séance ?
            </h1>
            <p className="text-sm text-[#4B3869] mt-2">
              avec <strong>{context.coach_name}</strong><br />
              {formatDateLongFR(context.date)} à {context.start_time}
            </p>
          </div>

          {/* Stars */}
          <div
            className="flex justify-center gap-2 mb-3"
            onMouseLeave={() => setHover(0)}
            data-testid="review-stars"
          >
            {[1, 2, 3, 4, 5].map(n => {
              const active = (hover || rating) >= n;
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`star-${n}`}
                  onMouseEnter={() => setHover(n)}
                  onClick={() => setRating(n)}
                  className="p-1 transition-transform active:scale-95 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] rounded"
                  aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
                >
                  <Star
                    className={`w-10 h-10 sm:w-11 sm:h-11 transition-all ${
                      active ? 'fill-[#D4AF37] text-[#D4AF37] drop-shadow-[0_2px_6px_rgba(212,175,55,0.5)]' : 'text-gray-200'
                    }`}
                    strokeWidth={1.5}
                  />
                </button>
              );
            })}
          </div>

          {/* Feedback label */}
          <div className="h-6 text-center mb-5">
            <AnimatePresence mode="wait">
              {activeMeta && (
                <motion.span
                  key={activeMeta.label}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className={`text-sm font-semibold ${activeMeta.color}`}
                >
                  {activeMeta.label}
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* Comment */}
          <label htmlFor="comment" className="block text-xs uppercase tracking-widest text-[#6D28D9] font-bold mb-2">
            Commentaire (optionnel)
          </label>
          <textarea
            id="comment"
            data-testid="review-comment"
            value={comment}
            onChange={e => setComment(e.target.value.slice(0, 1000))}
            placeholder="Qu'est-ce qui a été décisif pendant cette séance ?"
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#F9F7FC] text-[#1C1147] placeholder-gray-400 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#6D28D9]/20 focus:border-[#6D28D9] transition"
          />
          <div className="text-right text-[11px] text-gray-400 mt-1">{comment.length}/1000</div>

          {/* Valorization */}
          <div className="flex items-start gap-2 text-xs text-[#4B3869] bg-[#FBEAFF]/50 border border-violet-100 rounded-xl px-3 py-2.5 my-5">
            <Sparkles className="w-4 h-4 text-[#6D28D9] flex-shrink-0 mt-0.5" />
            <span>Votre retour aide à maintenir un haut niveau de qualité et à valoriser les coachs qui le méritent.</span>
          </div>

          {/* CTA */}
          <button
            onClick={submit}
            disabled={rating < 1 || submitting}
            data-testid="review-submit-btn"
            className="w-full py-3.5 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1A0F3A] font-bold text-sm shadow-lg shadow-[#D4AF37]/20 hover:shadow-xl hover:shadow-[#D4AF37]/30 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer mon avis'}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
